import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramHydronicHeatTransfer } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.hydronicHeatTransfer.v1';

type Fields = { gpm: string; tempDiff: string; heatLoad: string };
const EMPTY: Fields = { gpm: '', tempDiff: '', heatLoad: '' };

/**
 * Hydronic Heat Transfer — Q = GPM × 500 × ΔT
 * Bidirectional: fill any 2 of 3 fields.
 */
const HydronicHeatTransferCalculationsScreen = () => {
  const { isImperial, unit, toImp, fromImp } = useUnits();
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const dtToImp = (s: string) => {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return NaN;
    return isImperial ? n : n * 9 / 5;
  };
  const dtFromImp = (n: number, dec = 2) => {
    if (!Number.isFinite(n)) return '';
    return parseFloat((isImperial ? n : n * 5 / 9).toFixed(dec)).toString();
  };

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) { setError('Fill any 2 of the 3 fields to solve for the missing one.'); return; }
    if (filled === 3) { setError('All 3 values are filled — clear one to solve for it.'); return; }

    const g = toImp(fields.gpm, 'waterFlow');
    const t = dtToImp(fields.tempDiff);
    const q = toImp(fields.heatLoad, 'heatRate');

    if (fields.heatLoad === '') {
      if (!(g > 0) || !(t > 0)) { setError('Flow and ΔT must be greater than zero.'); return; }
      setSolvedFor('heatLoad');
      set({ heatLoad: fromImp(g * 500 * t, 'heatRate', 2) });
    } else if (fields.gpm === '') {
      if (!(t > 0)) { setError('ΔT must be greater than zero.'); return; }
      setSolvedFor('gpm');
      set({ gpm: fromImp(q / (500 * t), 'waterFlow', 2) });
    } else if (fields.tempDiff === '') {
      if (!(g > 0)) { setError('Flow must be greater than zero.'); return; }
      setSolvedFor('tempDiff');
      set({ tempDiff: dtFromImp(q / (500 * g)) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.gpm && { label: 'Flow', value: fields.gpm, unit: unit('waterFlow'), emphasis: solvedFor === 'gpm' },
    fields.tempDiff && { label: 'ΔT', value: fields.tempDiff, unit: unit('temperature'), emphasis: solvedFor === 'tempDiff' },
    fields.heatLoad && { label: 'Heating/Cooling Load', value: fields.heatLoad, unit: unit('heatRate'), emphasis: solvedFor === 'heatLoad' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Hydronic Heat Transfer"
      subtitle={isImperial ? 'Solve for load, flow, or ΔT using Q = GPM × 500 × ΔT' : 'Solve for load, flow, or ΔT using Q = L/min × 69.7 × ΔT'}
      formula={
        <>
          <FormulaLine>{isImperial ? 'Q = GPM × 500 × ΔT' : 'Q = L/min × 69.7 × ΔT'}</FormulaLine>
          <FormulaNote>
            {isImperial
              ? `Flow (${unit('waterFlow')}), ΔT (${unit('temperature')}). Constant 500 = 8.34 lb/gal × 60 min/hr (Imperial).`
              : `Flow (${unit('waterFlow')}), ΔT (${unit('temperature')}). Constant 69.7 = 4187 J/kg·°C × 1 kg/L ÷ 60 s/min (SI).`}
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formula: Q = GPM × 500 × ΔT' : 'Formula: Q = L/min × 69.7 × ΔT'}
      diagram={<DiagramHydronicHeatTransfer />}
      outputSlots={{
        gpm: fields.gpm ? toImp(fields.gpm, 'waterFlow') : undefined,
        delta_t_f: fields.tempDiff ? dtToImp(fields.tempDiff) : undefined,
        q_btuh: fields.heatLoad ? toImp(fields.heatLoad, 'heatRate') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.gpm != null && !fields.gpm) patch.gpm = fromImp(values.gpm, 'waterFlow', 2);
        if (values.delta_t_f != null && !fields.tempDiff) patch.tempDiff = dtFromImp(values.delta_t_f);
        if (values.q_btuh != null && !fields.heatLoad) patch.heatLoad = fromImp(values.q_btuh, 'heatRate', 2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Flow Rate" value={fields.gpm} onChangeText={(v) => set({ gpm: v })} rightText={unit('waterFlow')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Temperature Difference" value={fields.tempDiff} onChangeText={(v) => set({ tempDiff: v })} rightText={unit('temperature')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Heat Load" value={fields.heatLoad} onChangeText={(v) => set({ heatLoad: v })} rightText={unit('heatRate')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default HydronicHeatTransferCalculationsScreen;
